import React, { useContext, useMemo, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { Swiper } from 'antd-mobile';
import { PhysicalInfoDimension } from 'common-package';
import { PART_LIST } from '@/pages/ai-consultation/components/select-part';
import { setShowType, useWeightLossStore } from '../useWeightLossStore';
import { useBodyPainAdjustStore } from '../../body-pain-adjust/useBodyPainAdjustStore';
import { BodyChange3DContext, BODYPAIN_CARD_2_TEXT, formatHopesText, PAIN_LEVEL_2_PLAIN_TEXT } from '../../utils';

import cx from './index.module.scss';

interface Props {
  onClickClose?: () => void;
  isBodyPainAdjust?: boolean;
}

const InfoCard: React.FC<Props> = ({ onClickClose, isBodyPainAdjust }) => {
  const [showType] = useState(useWeightLossStore.use.showType());
  const [bodyPainShowType] = useState(useBodyPainAdjustStore.use.showType());
  const { bodyChangeInfo, weightLossDescText, bodyAdjustDescText } = useContext(BodyChange3DContext);

  const currentWeightLossInfo = useMemo(
    () => bodyChangeInfo?.weightLossList?.find(item => item.problem === showType),
    [bodyChangeInfo, showType]
  );
  const currentBodyPainInfo = useMemo(
    () => bodyChangeInfo?.painReliefList?.find(item => item.problem === bodyPainShowType),
    [bodyChangeInfo, bodyPainShowType]
  );

  const initValueText = useMemo(() => {
    if (isBodyPainAdjust) {
      const initVal = bodyChangeInfo?.painReliefList?.find(item => item?.problem === bodyPainShowType)?.initLevel;

      return (
        <span>
          {BODYPAIN_CARD_2_TEXT[bodyPainShowType]}：<br />
          {initVal || initVal === 0 ? PAIN_LEVEL_2_PLAIN_TEXT[initVal]?.replace('疼痛', '') : '暂无'}
        </span>
      );
    } else {
      const initVal = bodyChangeInfo?.weightLossList?.find(item => item?.problem === showType)?.initValue;

      return (
        <span>
          {PhysicalInfoDimension[showType]}：
          {initVal ? (
            <span>
              <span>{initVal / 10}</span>
              <span className={cx('normal-text')}>厘米</span>
            </span>
          ) : (
            '暂无'
          )}
        </span>
      );
    }
  }, [bodyChangeInfo, bodyPainShowType, isBodyPainAdjust, showType]);

  const currentValueText = useMemo(() => {
    if (isBodyPainAdjust) {
      const currentVal = bodyChangeInfo?.painReliefList?.find(item => item?.problem === bodyPainShowType)?.level;

      return (
        <span>
          {BODYPAIN_CARD_2_TEXT[bodyPainShowType]}：<br />
          {currentVal || currentVal === 0 ? PAIN_LEVEL_2_PLAIN_TEXT[currentVal]?.replace('疼痛', '') : '暂无'}
        </span>
      );
    } else {
      const currentVal = bodyChangeInfo?.weightLossList?.find(item => item?.problem === showType)?.currentValue;

      return (
        <span>
          {PhysicalInfoDimension[showType]}：
          {currentVal ? (
            <span>
              <span>{currentVal / 10}</span>
              <span className={cx('normal-text')}>厘米</span>
            </span>
          ) : (
            '暂无'
          )}
        </span>
      );
    }
  }, [bodyChangeInfo, bodyPainShowType, isBodyPainAdjust, showType]);

  return (
    <>
      <div className={cx('info-card-header')}>
        <div>
          {!!(isBodyPainAdjust ? bodyChangeInfo?.painReliefHopes?.length : bodyChangeInfo?.weightLossHopes?.length) && (
            <>
              <span>您的诉求是 </span>
              <span className={cx('highlight')}>
                {isBodyPainAdjust
                  ? formatHopesText(bodyChangeInfo?.painReliefHopes)
                  : bodyChangeInfo?.weightLossHopes
                      ?.map(item => PART_LIST?.find(item2 => item2.part === item)?.name)
                      ?.join('，')}
              </span>
              <br />
            </>
          )}
          <span>
            已
            {isBodyPainAdjust
              ? `进行磁震波${bodyChangeInfo?.shockwaveTreatmentCount}个疗程，${bodyAdjustDescText}`
              : `内源热能${bodyChangeInfo?.radioFrequencyTreatmentCount}个疗程，${weightLossDescText}`}
          </span>
        </div>
        <Icon
          name="close"
          className={cx('close-icon')}
          onClick={() => (onClickClose ? onClickClose?.() : setShowType('all'))}
        />
      </div>
      <div className={cx('info-card-image-list')}>
        <div className={cx('info-card-left')}>
          {isBodyPainAdjust ? (
            <div className={cx('body-pain-img-wrapper')}>
              <Swiper className={cx('image-swiper')}>
                {new Array(bodyPainShowType === 'shoulder' ? 4 : 3).fill('').map((_, index) => (
                  <Swiper.Item key={index} className={cx('swiper-item')}>
                    <div className={cx('img-item')}>
                      {currentBodyPainInfo?.initMeasureImages?.[index] ? (
                        <div className={cx('img-box')}>
                          <img
                            className={cx('upload-img')}
                            src={currentBodyPainInfo?.initMeasureImages?.[index]}
                            alt=""
                          />
                        </div>
                      ) : (
                        <div className={cx('empty')}>动作测试图片未上传</div>
                      )}
                    </div>
                    <div className={cx('img-item')}>
                      {currentBodyPainInfo?.measureImages?.[index] ? (
                        <div className={cx('img-box')}>
                          <img className={cx('upload-img')} src={currentBodyPainInfo?.measureImages?.[index]} alt="" />
                        </div>
                      ) : (
                        <div className={cx('empty')}>动作测试图片未上传</div>
                      )}
                    </div>
                  </Swiper.Item>
                ))}
              </Swiper>
            </div>
          ) : (
            <div className={cx('weightloss-image-wrapper')}>
              <div className={cx('image-item')}>
                {currentWeightLossInfo?.initImage ? (
                  <div className={cx('img-box')}>
                    <img className={cx('upload-img')} src={currentWeightLossInfo?.initImage} alt="" />
                  </div>
                ) : (
                  <div className={cx('empty-image')}>围度测量图片未上传</div>
                )}
              </div>
              <div className={cx('image-item')}>
                {currentWeightLossInfo?.currentImage ? (
                  <div className={cx('img-box')}>
                    <img src={currentWeightLossInfo?.currentImage} className={cx('upload-img')} alt="" />
                  </div>
                ) : (
                  <div className={cx('empty-image')}>围度测量图片未上传</div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className={cx('info-card-right')}>
          <div className={cx('detail-info')}>
            <div className={cx('main-info')}>
              <span>
                {isBodyPainAdjust ? '最严重' : '最高'}
                {initValueText}
              </span>
            </div>
            {isBodyPainAdjust
              ? !!(currentBodyPainInfo?.initLevel || currentBodyPainInfo?.initLevel === 0) && (
                  <div className={cx('sub-info')}>测试时间：{currentBodyPainInfo?.initMeasureTime}</div>
                )
              : !!currentWeightLossInfo?.initValue && (
                  <div className={cx('sub-info')}>测量时间：{currentWeightLossInfo?.initMeasureTime}</div>
                )}
          </div>
          <div className={cx('detail-info')}>
            <div className={cx('main-info')}>
              <span>目前{currentValueText}</span>
            </div>
            {isBodyPainAdjust
              ? !!(currentBodyPainInfo?.level || currentBodyPainInfo?.level === 0) && (
                  <div className={cx('sub-info')}>测试时间：{currentBodyPainInfo?.measureTime}</div>
                )
              : !!currentWeightLossInfo?.currentValue && (
                  <div className={cx('sub-info')}>测量时间：{currentWeightLossInfo?.measureTime}</div>
                )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InfoCard;
